import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Chip,
  InputAdornment,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  Paper,
  Divider,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ShoppingCart as ShoppingCartIcon,
  Edit as EditIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { api } from '../../utils/api';
import ImageCarousel from '../shop/ImageCarousel';

// 🔧 Funzione utility per formattare i prezzi in modo sicuro
const formatPrice = (price) => {
  if (price === null || price === undefined || isNaN(parseFloat(price))) {
    return '0.00';
  }
  return parseFloat(price).toFixed(2);
};

const CounterSales = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCustomDialog, setOpenCustomDialog] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [includeLabor, setIncludeLabor] = useState(false);
  const [customProduct, setCustomProduct] = useState({
    description: '',
    purchasePrice: '',
    sellingPrice: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const filteredProducts = useMemo(() => {
    const byCategory = selectedCategories.includes('all')
      ? products
      : products.filter((product) => selectedCategories.includes(product.category));

    return byCategory.filter(product => product.stock > 0 || product.isLabor);
  }, [products, selectedCategories]);

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    console.log('handleCategoryChange chiamato con valore:', value);

    // Il valore è sempre un array per Select multiple
    if (!Array.isArray(value) || value.length === 0) {
      setSelectedCategories(['all']);
      return;
    }

    const isAllInValue = value.includes('all');
    const categoriesOnly = value.filter((cat) => cat !== 'all');
    
    // Se 'all' è presente insieme ad altre categorie, rimuovi 'all'
    if (isAllInValue && categoriesOnly.length > 0) {
      setSelectedCategories(categoriesOnly);
      return;
    }
    
    // Se solo 'all' è selezionato
    if (isAllInValue && categoriesOnly.length === 0) {
      setSelectedCategories(['all']);
      return;
    }
    
    // Se non c'è 'all' ma ci sono categorie
    if (!isAllInValue && categoriesOnly.length > 0) {
      setSelectedCategories(categoriesOnly);
      return;
    }
    
    // Fallback: se non c'è niente, seleziona 'all'
    setSelectedCategories(['all']);
  };

  const isAllSelected = selectedCategories.includes('all');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [searchTerm, selectedCategories]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/products/categories/all');
      // Ordina le categorie alfabeticamente
      const sortedCategories = response.data.sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
      setCategories(sortedCategories);
    } catch (error) {
      console.error('Errore nel caricamento delle categorie:', error);
      setError('Errore nel caricamento delle categorie');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const categoryFilters = selectedCategories.filter((category) => category !== 'all');
      const params = {
        all: 'true', // Richiedi tutti i prodotti per le vendite
        search: searchTerm || undefined
      };

      if (categoryFilters.length === 1) {
        params.category = categoryFilters[0];
      }

      const response = await api.get('/products', { params });
      
      // Con all=true, la risposta è direttamente un array
      if (Array.isArray(response.data)) {
        setProducts(response.data);
      } else if (response.data && response.data.products) {
        setProducts(response.data.products);
      } else {
        console.error('Formato risposta API prodotti non valido:', response.data);
        setProducts([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento dei prodotti:', error);
      setError('Errore nel caricamento dei prodotti');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setIncludeLabor(product.isLabor || false);
    setOpenDialog(true);
  };

  const handleOpenCustomDialog = () => {
    setCustomProduct({
      description: '',
      purchasePrice: '0',
      sellingPrice: ''
    });
    setOpenCustomDialog(true);
  };

  const handleCloseCustomDialog = () => {
    setOpenCustomDialog(false);
  };

  const validateCustomProduct = () => {
    if (!customProduct.description.trim()) {
      setSnackbar({
        open: true,
        message: 'La descrizione è obbligatoria',
        severity: 'error'
      });
      return false;
    }
    if (!customProduct.sellingPrice || parseFloat(customProduct.sellingPrice) <= 0) {
      setSnackbar({
        open: true,
        message: 'Il prezzo di vendita deve essere maggiore di zero',
        severity: 'error'
      });
      return false;
    }
    return true;
  };

  const handleAddCustomToCart = () => {
    if (!validateCustomProduct()) return;

    // Assicura che il prezzo di acquisto sia valorizzato e numerico
    const purchasePrice = parseFloat(customProduct.purchasePrice);
    if (isNaN(purchasePrice) || purchasePrice < 0) {
      setSnackbar({
        open: true,
        message: 'Inserisci un prezzo di acquisto valido per l\'articolo personalizzato',
        severity: 'error'
      });
      return;
    }

    const customCode = getCustomCode();
    const customCartItem = {
      productId: customCode,
      code: customCode,
      description: customProduct.description,
      quantity: 1,
      price: parseFloat(customProduct.sellingPrice) || 0,
      purchasePrice: purchasePrice,
      includeLabor: false,
      isCustom: true,
      total: parseFloat(customProduct.sellingPrice) || 0
    };

    console.log('Dati articolo personalizzato per il carrello:', customCartItem);

    // Recupera il carrello esistente o inizializza un nuovo array
    const currentCart = JSON.parse(localStorage.getItem('cart') || '[]');
    
    // Aggiungi il nuovo item
    currentCart.push(customCartItem);
    
    // Salva il carrello aggiornato
    localStorage.setItem('cart', JSON.stringify(currentCart));

    setSnackbar({
      open: true,
      message: 'Articolo personalizzato aggiunto al carrello',
      severity: 'success'
    });

    setOpenCustomDialog(false);
  };

  const handleAddToCart = () => {
    if (!selectedProduct) return;

    const cartItem = {
      productId: selectedProduct.id,
      description: selectedProduct.description,
      quantity: quantity,
      price: includeLabor ? (selectedProduct.sellingPriceWithAssembly || 0) : (selectedProduct.sellingPrice || 0),
      includeLabor: includeLabor,
      total: (includeLabor ? (selectedProduct.sellingPriceWithAssembly || 0) : (selectedProduct.sellingPrice || 0)) * quantity
    };

    // Recupera il carrello esistente o inizializza un nuovo array
    const currentCart = JSON.parse(localStorage.getItem('cart') || '[]');
    
    // Aggiungi il nuovo item
    currentCart.push(cartItem);
    
    // Salva il carrello aggiornato
    localStorage.setItem('cart', JSON.stringify(currentCart));

    setSnackbar({
      open: true,
      message: 'Prodotto aggiunto al carrello',
      severity: 'success'
    });

    setOpenDialog(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCustomProductChange = (field) => (e) => {
    setCustomProduct({
      ...customProduct,
      [field]: field === 'description' ? e.target.value.toUpperCase() : e.target.value
    });
  };

  // Funzione per generare codice custom
  function getCustomCode() {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const aa = String(now.getFullYear()).slice(-2);
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `ART-PER-${dd}${mm}${aa}${hh}${min}${ss}`;
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Filtri */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Cerca prodotto per nome, descrizione o codice..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Categoria</InputLabel>
            <Select
              multiple
              value={selectedCategories}
              onChange={handleCategoryChange}
              label="Categoria"
              renderValue={(selected) => {
                if (selected.length === 0 || selected.includes('all')) {
                  return 'Tutte le categorie';
                }
                if (selected.length <= 3) {
                  return selected.join(', ');
                }
                return `${selected.slice(0, 2).join(', ')} +${selected.length - 2}`;
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    width: 600,
                    backgroundColor: '#fff',
                    '& .MuiMenuItem-root': {
                      color: '#000 !important',
                      '& .MuiListItemText-primary': {
                        color: '#000 !important',
                        fontSize: '0.875rem !important'
                      },
                      '& .MuiCheckbox-root': {
                        color: '#1976d2'
                      }
                    }
                  }
                },
                MenuListProps: {
                  sx: {
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    columnGap: 1,
                    rowGap: 0,
                    backgroundColor: '#fff',
                    padding: '8px'
                  }
                },
                keepMounted: false,
                disableAutoFocusItem: true
              }}
            >
              <MenuItem 
                value="all"
                sx={{ 
                  color: '#000 !important',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  },
                  '& .MuiListItemText-primary': {
                    color: '#000 !important'
                  }
                }}
              >
                <Checkbox 
                  checked={isAllSelected}
                />
                <ListItemText 
                  primary="Tutte le categorie"
                  primaryTypographyProps={{ 
                    sx: { 
                      color: '#000 !important',
                      fontWeight: isAllSelected ? 'bold' : 'normal',
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </MenuItem>
              {categories.map((category) => {
                const isSelected = !isAllSelected && selectedCategories.includes(category);
                return (
                  <MenuItem 
                    key={category} 
                    value={category}
                    sx={{ 
                      color: '#000 !important',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      '& .MuiListItemText-primary': {
                        color: '#000 !important'
                      }
                    }}
                  >
                    <Checkbox 
                      checked={isSelected}
                    />
                    <ListItemText 
                      primary={category}
                      primaryTypographyProps={{ 
                        sx: { 
                          color: '#000 !important',
                          fontWeight: isSelected ? 'bold' : 'normal',
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Products grid */}
      <Grid container spacing={2}>
        {/* Card per articolo personalizzato - sempre come primo elemento */}
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Card 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6
              },
              transform: 'scale(0.85)',
              transformOrigin: 'top left',
              bgcolor: '#f0f7ff',
              border: '1px dashed #1976d2'
            }}
            onClick={handleOpenCustomDialog}
          >
            <CardMedia
              component="div"
              sx={{ 
                height: 140, 
                bgcolor: '#e3f2fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EditIcon sx={{ fontSize: 60, color: '#1976d2' }} />
            </CardMedia>
            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                Articolo Personalizzato
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '1rem' }}>
                Crea un articolo su misura
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Chip 
                    label="Personalizzato" 
                    size="small" 
                    color="primary" 
                    sx={{ mt: 1 }} 
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Prodotti dal catalogo */}
        {filteredProducts && filteredProducts
          .map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={product.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6
                },
                transform: 'scale(0.85)', // Riduce l'altezza del 15%
                transformOrigin: 'top left'
              }}
              onClick={() => handleProductClick(product)}
            >
            <ImageCarousel
              imageUrls={product.image}
              productName={product.description}
              height={140}
              objectFit="contain"
              sx={{ bgcolor: '#f5f5f5' }}
            />
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Typography variant="h6" gutterBottom noWrap sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  {product.description}
                </Typography>
                {product.code && (
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                    Codice: {product.code}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '1rem' }}>
                  Categoria: {product.category}
                </Typography>
                <Grid container spacing={1}>
                  {!product.isLabor && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                        Prezzo di vendita
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                        €{formatPrice(product.sellingPrice)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      {product.isLabor ? 'Prezzo servizio' : 'Prezzo con manodopera'}
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      €{formatPrice(product.sellingPriceWithAssembly)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      {product.isLabor ? 'Servizio (disponibile)' : `Disponibili: ${product.stock || 0} pezzi`}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Product Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Dettagli Prodotto</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ImageCarousel
                  imageUrls={selectedProduct.image}
                  productName={selectedProduct.description}
                  height={300}
                  objectFit="contain"
                  sx={{ borderRadius: 1, bgcolor: '#f5f5f5' }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {selectedProduct.description}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Categoria: {selectedProduct.category}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedProduct.isLabor ? 'Servizio (disponibile)' : `Disponibili: ${selectedProduct.stock || 0} pezzi`}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {!selectedProduct.isLabor && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Prezzo di vendita
                      </Typography>
                      <Typography variant="h6" color="primary">
                        €{formatPrice(selectedProduct.sellingPrice)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {selectedProduct.isLabor ? 'Prezzo servizio' : 'Prezzo con manodopera'}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      €{formatPrice(selectedProduct.sellingPriceWithAssembly)}
                    </Typography>
                  </Grid>
                </Grid>

                {!selectedProduct.isLabor && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeLabor}
                        onChange={(e) => setIncludeLabor(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={`Includi manodopera`}
                    sx={{ mt: 2, display: 'block' }}
                  />
                )}
                
                <TextField
                  type="number"
                  label="Quantità"
                  value={quantity}
                  onChange={(e) => {
                    const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
                    if (selectedProduct.isLabor || newQuantity <= selectedProduct.stock) {
                      setQuantity(newQuantity);
                    } else {
                      setSnackbar({
                        open: true,
                        message: 'Quantità non disponibile',
                        severity: 'error'
                      });
                    }
                  }}
                  fullWidth
                  sx={{ mt: 2 }}
                  InputProps={{ 
                    inputProps: { 
                      min: 1,
                      max: selectedProduct.isLabor ? undefined : selectedProduct.stock
                    } 
                  }}
                />

                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                  Totale: €{formatPrice((includeLabor ? selectedProduct.sellingPriceWithAssembly : selectedProduct.sellingPrice) * quantity)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button 
            onClick={handleAddToCart} 
            variant="contained" 
            startIcon={<ShoppingCartIcon />}
          >
            Aggiungi al Carrello
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Product Dialog */}
      <Dialog open={openCustomDialog} onClose={handleCloseCustomDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Articolo Personalizzato</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrizione"
                value={customProduct.description}
                onChange={handleCustomProductChange('description')}
                margin="normal"
                required
                placeholder="Inserisci una descrizione dettagliata"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prezzo di acquisto (€)"
                type="number"
                value={customProduct.purchasePrice}
                onChange={handleCustomProductChange('purchasePrice')}
                margin="normal"
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
                placeholder="0.00"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prezzo di vendita (€)"
                type="number"
                value={customProduct.sellingPrice}
                onChange={handleCustomProductChange('sellingPrice')}
                margin="normal"
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
                placeholder="0.00"
              />
            </Grid>
            <Grid item xs={12}>
              {customProduct.sellingPrice && (
                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                  Totale: €{formatPrice(parseFloat(customProduct.sellingPrice))}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomDialog}>Annulla</Button>
          <Button 
            onClick={handleAddCustomToCart} 
            variant="contained" 
            color="primary" 
            startIcon={<ShoppingCartIcon />}
          >
            Aggiungi al Carrello
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CounterSales; 